import { Task } from '../types';

// Helper function to get a future date
export const getFutureDate = (daysFromNow: number) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString();
};

// Helper to create a task template
const createTaskTemplate = (
  id: string,
  title: string,
  description: string,
  assignedTo: string,
  dueDays: number,
  priority: Task['priority'] = 'medium',
  status: Task['status'] = 'pending',
  stageId: string
): Task => ({
  id,
  title,
  description,
  assignedTo,
  dueDate: {
    type: 'relative',
    value: dueDays,
    unit: 'days'
  },
  status,
  priority,
  stageId,
});

// Task templates organized by stage
export const taskTemplates: Record<string, Task[]> = {
  // Pre-Case Stage Tasks
  'new-lead': [
    createTaskTemplate(
      'new-lead-1',
      'Initial Client Contact',
      'Make first contact with potential client to gather basic information',
      '2',
      2,
      'high',
      'pending',
      'new-lead'
    ),
    createTaskTemplate(
      'new-lead-2',
      'Basic Case Assessment',
      'Review initial information to determine case viability',
      '1',
      3,
      'medium',
      'pending',
      'new-lead'
    ),
  ],

  'pending-intake': [
    createTaskTemplate(
      'pending-intake-1',
      'Schedule Intake Meeting',
      'Set up initial consultation with potential client',
      '2',
      4,
      'high',
      'pending',
      'pending-intake'
    ),
    createTaskTemplate(
      'pending-intake-2',
      'Prepare Intake Forms',
      'Customize intake questionnaire based on case type',
      '2',
      5,
      'medium',
      'pending',
      'pending-intake'
    ),
  ],

  'conflict-check': [
    createTaskTemplate(
      'conflict-check-1',
      'Run Conflict Check',
      'Check for potential conflicts with existing or past clients',
      '2',
      2,
      'high',
      'pending',
      'conflict-check'
    ),
    createTaskTemplate(
      'conflict-check-2',
      'Document Search',
      'Search firm database for related cases or parties',
      '2',
      3,
      'medium',
      'pending',
      'conflict-check'
    ),
  ],

  'intake-completed': [
    createTaskTemplate(
      'intake-completed-1',
      'Review Intake Documents',
      'Complete review of all submitted intake forms and documentation',
      '1',
      4,
      'high',
      'pending',
      'intake-completed'
    ),
    createTaskTemplate(
      'intake-completed-2',
      'Initial Case Strategy',
      'Develop preliminary case strategy based on intake information',
      '1',
      5,
      'high',
      'pending',
      'intake-completed'
    ),
  ],

  'awaiting-retainer': [
    createTaskTemplate(
      'awaiting-retainer-1',
      'Prepare Retainer Agreement',
      'Draft and review retainer agreement for client',
      '1',
      3,
      'high',
      'pending',
      'awaiting-retainer'
    ),
    createTaskTemplate(
      'awaiting-retainer-2',
      'Send Fee Schedule',
      'Prepare and send detailed fee schedule to client',
      '2',
      4,
      'medium',
      'pending',
      'awaiting-retainer'
    ),
  ],

  'retainer-signed': [
    createTaskTemplate(
      'retainer-signed-1',
      'Process Retainer Payment',
      'Process and record the retainer payment in the billing system',
      '2',
      1,
      'high',
      'pending',
      'retainer-signed'
    ),
    createTaskTemplate(
      'retainer-signed-2',
      'Create Client File',
      'Set up physical and digital client files with all intake documents and retainer agreement',
      '2',
      1,
      'high',
      'pending',
      'retainer-signed'
    ),
    createTaskTemplate(
      'retainer-signed-3',
      'Send Welcome Package',
      'Prepare and send client welcome package with important information and next steps',
      '2',
      2,
      'medium',
      'pending',
      'retainer-signed'
    ),
    createTaskTemplate(
      'retainer-signed-4',
      'Schedule Initial Strategy Meeting',
      'Set up first full team meeting to discuss case strategy and assign responsibilities',
      '2',
      3,
      'high',
      'pending',
      'retainer-signed'
    ),
    createTaskTemplate(
      'retainer-signed-5',
      'Request Client Records',
      'Send formal requests for all relevant client records and documentation',
      '2',
      3,
      'high',
      'pending',
      'retainer-signed'
    ),
    createTaskTemplate(
      'retainer-signed-6',
      'Set Up Case Calendar',
      'Create case calendar with important dates, deadlines, and statute of limitations',
      '1',
      2,
      'high',
      'pending',
      'retainer-signed'
    ),
    createTaskTemplate(
      'retainer-signed-7',
      'Configure Billing Profile',
      'Set up client billing profile and configure any special billing arrangements',
      '2',
      2,
      'medium',
      'pending',
      'retainer-signed'
    ),
  ],

  'case-opened': [
    createTaskTemplate(
      'case-opened-1',
      'Create Case File',
      'Set up physical and digital case files',
      '2',
      2,
      'high',
      'pending',
      'case-opened'
    ),
    createTaskTemplate(
      'case-opened-2',
      'Assign Team Members',
      'Designate team roles and responsibilities',
      '3',
      3,
      'high',
      'pending',
      'case-opened'
    ),
  ],

  'initial-review': [
    createTaskTemplate(
      'initial-review-1',
      'Review Case Materials',
      'Comprehensive review of all available case materials',
      '1',
      5,
      'high',
      'pending',
      'initial-review'
    ),
    createTaskTemplate(
      'initial-review-2',
      'Identify Key Issues',
      'Document primary legal issues and potential challenges',
      '1',
      7,
      'high',
      'pending',
      'initial-review'
    ),
  ],

  'awaiting-documents': [
    createTaskTemplate(
      'awaiting-docs-1',
      'Document Request List',
      'Create comprehensive list of required documents',
      '2',
      3,
      'high',
      'pending',
      'awaiting-documents'
    ),
    createTaskTemplate(
      'awaiting-docs-2',
      'Send Document Requests',
      'Send formal requests to all relevant parties',
      '2',
      5,
      'high',
      'pending',
      'awaiting-documents'
    ),
    createTaskTemplate(
      'awaiting-docs-3',
      'Follow Up on Requests',
      'Check status of document requests and send reminders',
      '2',
      10,
      'medium',
      'pending',
      'awaiting-documents'
    ),
  ],

  'documents-received': [
    createTaskTemplate(
      'docs-received-1',
      'Document Inventory',
      'Create inventory of all received documents',
      '2',
      2,
      'high',
      'pending',
      'documents-received'
    ),
    createTaskTemplate(
      'docs-received-2',
      'Initial Document Review',
      'Preliminary review of received documents',
      '1',
      5,
      'high',
      'pending',
      'documents-received'
    ),
    createTaskTemplate(
      'docs-received-3',
      'Identify Missing Documents',
      'Create list of any missing or incomplete documents',
      '2',
      3,
      'medium',
      'pending',
      'documents-received'
    ),
  ],

  'investigation': [
    createTaskTemplate(
      'investigation-1',
      'Witness Interviews',
      'Conduct interviews with key witnesses',
      '1',
      14,
      'high',
      'pending',
      'investigation'
    ),
    createTaskTemplate(
      'investigation-2',
      'Evidence Collection',
      'Gather and organize all relevant evidence',
      '2',
      10,
      'high',
      'pending',
      'investigation'
    ),
  ],

  'demand-letter': [
    createTaskTemplate(
      'demand-letter-1',
      'Draft Demand Letter',
      'Prepare comprehensive demand letter outlining claims and demands',
      '1',
      7,
      'high',
      'pending',
      'demand-letter'
    ),
    createTaskTemplate(
      'demand-letter-2',
      'Calculate Damages',
      'Detailed calculation of all damages and losses',
      '3',
      5,
      'high',
      'pending',
      'demand-letter'
    ),
  ],

  'negotiation': [
    createTaskTemplate(
      'negotiation-1',
      'Review Settlement Options',
      'Analyze potential settlement scenarios and strategies',
      '1',
      5,
      'high',
      'pending',
      'negotiation'
    ),
    createTaskTemplate(
      'negotiation-2',
      'Client Consultation',
      'Discuss settlement parameters with client',
      '1',
      3,
      'high',
      'pending',
      'negotiation'
    ),
  ],

  'settlement-offer-received': [
    createTaskTemplate(
      'offer-received-1',
      'Analyze Settlement Offer',
      'Review and analyze terms of the settlement offer',
      '1',
      3,
      'high',
      'pending',
      'settlement-offer-received'
    ),
    createTaskTemplate(
      'offer-received-2',
      'Client Meeting',
      'Meet with client to discuss settlement offer',
      '1',
      5,
      'high',
      'pending',
      'settlement-offer-received'
    ),
    createTaskTemplate(
      'offer-received-3',
      'Prepare Response',
      'Draft response to settlement offer',
      '1',
      7,
      'high',
      'pending',
      'settlement-offer-received'
    ),
  ],

  'settlement-offer-rejected': [
    createTaskTemplate(
      'offer-rejected-1',
      'Document Rejection',
      'Prepare formal rejection of settlement offer',
      '1',
      2,
      'high',
      'pending',
      'settlement-offer-rejected'
    ),
    createTaskTemplate(
      'offer-rejected-2',
      'Strategy Review',
      'Review and update case strategy',
      '1',
      5,
      'high',
      'pending',
      'settlement-offer-rejected'
    ),
  ],

  'mediation-scheduled': [
    createTaskTemplate(
      'mediation-scheduled-1',
      'Prepare Mediation Brief',
      'Draft and finalize mediation brief',
      '1',
      7,
      'high',
      'pending',
      'mediation-scheduled'
    ),
    createTaskTemplate(
      'mediation-scheduled-2',
      'Client Preparation',
      'Prepare client for mediation process',
      '1',
      3,
      'high',
      'pending',
      'mediation-scheduled'
    ),
    createTaskTemplate(
      'mediation-scheduled-3',
      'Evidence Organization',
      'Organize key evidence for mediation',
      '2',
      5,
      'medium',
      'pending',
      'mediation-scheduled'
    ),
  ],

  'mediation-in-progress': [
    createTaskTemplate(
      'mediation-progress-1',
      'Document Discussions',
      'Record and document all mediation discussions',
      '1',
      1,
      'high',
      'pending',
      'mediation-in-progress'
    ),
    createTaskTemplate(
      'mediation-progress-2',
      'Client Updates',
      'Keep client informed of mediation progress',
      '1',
      1,
      'high',
      'pending',
      'mediation-in-progress'
    ),
  ],

  'mediation-completed': [
    createTaskTemplate(
      'mediation-complete-1',
      'Mediation Summary',
      'Prepare summary of mediation outcomes',
      '1',
      2,
      'high',
      'pending',
      'mediation-completed'
    ),
    createTaskTemplate(
      'mediation-complete-2',
      'Next Steps Plan',
      'Develop plan for next steps based on mediation outcome',
      '1',
      3,
      'high',
      'pending',
      'mediation-completed'
    ),
  ],

  'lawsuit-filed': [
    createTaskTemplate(
      'lawsuit-filed-1',
      'File Service Instructions',
      'Prepare instructions for service of process',
      '2',
      2,
      'high',
      'pending',
      'lawsuit-filed'
    ),
    createTaskTemplate(
      'lawsuit-filed-2',
      'Calendar Deadlines',
      'Set up calendar with all relevant deadlines',
      '2',
      1,
      'high',
      'pending',
      'lawsuit-filed'
    ),
  ],

  'service-pending': [
    createTaskTemplate(
      'service-pending-1',
      'Monitor Service Status',
      'Track and follow up on service attempts',
      '2',
      7,
      'high',
      'pending',
      'service-pending'
    ),
    createTaskTemplate(
      'service-pending-2',
      'Alternative Service Plan',
      'Develop backup plan for service if needed',
      '1',
      10,
      'medium',
      'pending',
      'service-pending'
    ),
  ],

  'awaiting-response': [
    createTaskTemplate(
      'awaiting-response-1',
      'Monitor Response Deadline',
      'Track defendant\'s response deadline',
      '2',
      20,
      'high',
      'pending',
      'awaiting-response'
    ),
    createTaskTemplate(
      'awaiting-response-2',
      'Default Preparation',
      'Prepare default documents if needed',
      '1',
      25,
      'medium',
      'pending',
      'awaiting-response'
    ),
  ],

  'discovery-phase': [
    createTaskTemplate(
      'discovery-1',
      'Review Medical Records',
      'Review and analyze client\'s medical records from hospital',
      '1',
      5,
      'high',
      'pending',
      'discovery-phase'
    ),
    createTaskTemplate(
      'discovery-2',
      'Schedule Expert Witness',
      'Contact and schedule meeting with construction safety expert',
      '2',
      7,
      'medium',
      'pending',
      'discovery-phase'
    ),
    createTaskTemplate(
      'discovery-3',
      'Draft Interrogatories',
      'Prepare initial set of interrogatories for defendant',
      '1',
      3,
      'high',
      'pending',
      'discovery-phase'
    ),
  ],

  'motion-practice': [
    createTaskTemplate(
      'motion-practice-1',
      'Draft Motion',
      'Prepare and draft required motion',
      '1',
      7,
      'high',
      'pending',
      'motion-practice'
    ),
    createTaskTemplate(
      'motion-practice-2',
      'Supporting Documents',
      'Gather and prepare supporting exhibits',
      '2',
      5,
      'high',
      'pending',
      'motion-practice'
    ),
  ],

  'pre-trial-conference': [
    createTaskTemplate(
      'pre-trial-1',
      'Conference Preparation',
      'Prepare for pre-trial conference',
      '1',
      5,
      'high',
      'pending',
      'pre-trial-conference'
    ),
    createTaskTemplate(
      'pre-trial-2',
      'Required Documents',
      'Prepare required pre-trial documents',
      '2',
      7,
      'high',
      'pending',
      'pre-trial-conference'
    ),
  ],

  'trial-scheduled': [
    createTaskTemplate(
      'trial-scheduled-1',
      'Trial Calendar',
      'Create detailed trial preparation calendar',
      '2',
      2,
      'high',
      'pending',
      'trial-scheduled'
    ),
    createTaskTemplate(
      'trial-scheduled-2',
      'Witness Coordination',
      'Begin coordinating witness schedules',
      '2',
      5,
      'high',
      'pending',
      'trial-scheduled'
    ),
  ],

  'trial-preparation': [
    createTaskTemplate(
      'trial-prep-1',
      'Prepare Witness List',
      'Finalize list of witnesses for trial',
      '1',
      10,
      'high',
      'pending',
      'trial-preparation'
    ),
    createTaskTemplate(
      'trial-prep-2',
      'Organize Exhibits',
      'Compile and organize all trial exhibits',
      '2',
      7,
      'high',
      'pending',
      'trial-preparation'
    ),
    createTaskTemplate(
      'trial-prep-3',
      'Draft Opening Statement',
      'Prepare opening statement for trial',
      '1',
      5,
      'high',
      'pending',
      'trial-preparation'
    ),
  ],

  'trial-in-progress': [
    createTaskTemplate(
      'trial-progress-1',
      'Daily Trial Notes',
      'Document daily trial proceedings',
      '2',
      1,
      'high',
      'pending',
      'trial-in-progress'
    ),
    createTaskTemplate(
      'trial-progress-2',
      'Witness Preparation',
      'Prepare upcoming witnesses',
      '1',
      2,
      'high',
      'pending',
      'trial-in-progress'
    ),
  ],

  'verdict-pending': [
    createTaskTemplate(
      'verdict-pending-1',
      'Monitor Status',
      'Monitor court for verdict',
      '2',
      1,
      'high',
      'pending',
      'verdict-pending'
    ),
    createTaskTemplate(
      'verdict-pending-2',
      'Prepare Response Options',
      'Draft responses for possible verdicts',
      '1',
      3,
      'medium',
      'pending',
      'verdict-pending'
    ),
  ],

  'judgment-issued': [
    createTaskTemplate(
      'judgment-1',
      'Review Judgment',
      'Analyze court\'s judgment',
      '1',
      2,
      'high',
      'pending',
      'judgment-issued'
    ),
    createTaskTemplate(
      'judgment-2',
      'Client Meeting',
      'Meet with client to discuss judgment',
      '1',
      3,
      'high',
      'pending',
      'judgment-issued'
    ),
  ],

  'post-trial-motions': [
    createTaskTemplate(
      'post-trial-1',
      'Draft Motion',
      'Prepare post-trial motion',
      '1',
      7,
      'high',
      'pending',
      'post-trial-motions'
    ),
    createTaskTemplate(
      'post-trial-2',
      'Supporting Documents',
      'Gather supporting documentation',
      '2',
      5,
      'high',
      'pending',
      'post-trial-motions'
    ),
  ],

  'appeal-filed': [
    createTaskTemplate(
      'appeal-filed-1',
      'Notice of Appeal',
      'File notice of appeal',
      '1',
      2,
      'high',
      'pending',
      'appeal-filed'
    ),
    createTaskTemplate(
      'appeal-filed-2',
      'Record Preparation',
      'Begin preparing appellate record',
      '2',
      7,
      'high',
      'pending',
      'appeal-filed'
    ),
  ],

  'appeal-in-progress': [
    createTaskTemplate(
      'appeal-progress-1',
      'Draft Brief',
      'Prepare appellate brief',
      '1',
      30,
      'high',
      'pending',
      'appeal-in-progress'
    ),
    createTaskTemplate(
      'appeal-progress-2',
      'Oral Argument Prep',
      'Prepare for oral argument',
      '1',
      14,
      'high',
      'pending',
      'appeal-in-progress'
    ),
  ],

  'appeal-decided': [
    createTaskTemplate(
      'appeal-decided-1',
      'Review Decision',
      'Analyze appellate decision',
      '1',
      2,
      'high',
      'pending',
      'appeal-decided'
    ),
    createTaskTemplate(
      'appeal-decided-2',
      'Client Meeting',
      'Meet with client to discuss outcome',
      '1',
      3,
      'high',
      'pending',
      'appeal-decided'
    ),
  ],

  'settlement-draft': [
    createTaskTemplate(
      'settlement-1',
      'Draft Settlement Agreement',
      'Prepare initial settlement agreement draft',
      '1',
      5,
      'high',
      'pending',
      'settlement-draft'
    ),
    createTaskTemplate(
      'settlement-2',
      'Calculate Final Damages',
      'Finalize damages calculation for settlement',
      '3',
      3,
      'high',
      'pending',
      'settlement-draft'
    ),
  ],

  'settlement-signed': [
    createTaskTemplate(
      'settlement-signed-1',
      'Process Agreement',
      'File settlement agreement with court',
      '2',
      2,
      'high',
      'pending',
      'settlement-signed'
    ),
    createTaskTemplate(
      'settlement-signed-2',
      'Payment Instructions',
      'Provide payment instructions to defendant',
      '2',
      3,
      'high',
      'pending',
      'settlement-signed'
    ),
  ],

  'awaiting-payment': [
    createTaskTemplate(
      'payment-1',
      'Monitor Payment',
      'Track settlement payment status',
      '2',
      14,
      'high',
      'pending',
      'awaiting-payment'
    ),
    createTaskTemplate(
      'payment-2',
      'Follow Up',
      'Send payment reminders if needed',
      '2',
      21,
      'medium',
      'pending',
      'awaiting-payment'
    ),
  ],

  'payment-received': [
    createTaskTemplate(
      'payment-received-1',
      'Process Payment',
      'Process and record settlement payment',
      '2',
      1,
      'high',
      'pending',
      'payment-received'
    ),
    createTaskTemplate(
      'payment-received-2',
      'Prepare Distribution',
      'Calculate final distribution amounts',
      '3',
      2,
      'high',
      'pending',
      'payment-received'
    ),
  ],

  'disbursement-in-progress': [
    createTaskTemplate(
      'disbursement-1',
      'Process Disbursements',
      'Process payments to all parties',
      '2',
      3,
      'high',
      'pending',
      'disbursement-in-progress'
    ),
    createTaskTemplate(
      'disbursement-2',
      'Update Records',
      'Update accounting records',
      '2',
      2,
      'high',
      'pending',
      'disbursement-in-progress'
    ),
  ],

  'disbursement-completed': [
    createTaskTemplate(
      'disbursement-complete-1',
      'Final Accounting',
      'Prepare final accounting report',
      '2',
      2,
      'high',
      'pending',
      'disbursement-completed'
    ),
    createTaskTemplate(
      'disbursement-complete-2',
      'Close Financial Records',
      'Close all financial records for case',
      '2',
      3,
      'high',
      'pending',
      'disbursement-completed'
    ),
  ],

  'case-dismissed': [
    createTaskTemplate(
      'dismissed-1',
      'File Closing Documents',
      'Prepare and file case closing documents',
      '2',
      3,
      'high',
      'pending',
      'case-dismissed'
    ),
    createTaskTemplate(
      'dismissed-2',
      'Client Notification',
      'Notify client of case dismissal',
      '1',
      2,
      'high',
      'pending',
      'case-dismissed'
    ),
  ],

  'closed-successful': [
    createTaskTemplate(
      'closed-success-1',
      'Final Client Meeting',
      'Conduct final client meeting',
      '1',
      5,
      'high',
      'pending',
      'closed-successful'
    ),
    createTaskTemplate(
      'closed-success-2',
      'Archive Case Files',
      'Prepare files for archiving',
      '2',
      7,
      'medium',
      'pending',
      'closed-successful'
    ),
  ],

  'closed-unsuccessful': [
    createTaskTemplate(
      'closed-fail-1',
      'Client Debrief',
      'Meet with client to discuss outcome',
      '1',
      3,
      'high',
      'pending',
      'closed-unsuccessful'
    ),
    createTaskTemplate(
      'closed-fail-2',
      'Case Review',
      'Conduct internal case review',
      '3',
      5,
      'medium',
      'pending',
      'closed-unsuccessful'
    ),
  ],

  'closed-withdrawn': [
    createTaskTemplate(
      'withdrawn-1',
      'Withdrawal Documentation',
      'Prepare withdrawal documentation',
      '1',
      3,
      'high',
      'pending',
      'closed-withdrawn'
    ),
    createTaskTemplate(
      'withdrawn-2',
      'Return Materials',
      'Return client materials',
      '2',
      5,
      'high',
      'pending',
      'closed-withdrawn'
    ),
  ],

  'closed-transferred': [
    createTaskTemplate(
      'transferred-1',
      'Transfer Documents',
      'Prepare case transfer documents',
      '2',
      3,
      'high',
      'pending',
      'closed-transferred'
    ),
    createTaskTemplate(
      'transferred-2',
      'File Transfer',
      'Transfer all case files',
      '2',
      5,
      'high',
      'pending',
      'closed-transferred'
    ),
  ],

  'archived': [
    createTaskTemplate(
      'archived-1',
      'Archive Preparation',
      'Prepare files for archiving',
      '2',
      5,
      'medium',
      'pending',
      'archived'
    ),
    createTaskTemplate(
      'archived-2',
      'Archive Transfer',
      'Transfer files to archive storage',
      '2',
      7,
      'medium',
      'pending',
      'archived'
    ),
  ],
};

// Function to generate tasks for a stage
export const generateStageTasks = (stageId: string): Task[] => {
  const templates = taskTemplates[stageId];
  if (!templates) return [];

  return templates.map(template => ({
    ...template,
    id: `${stageId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    dueDate: template.dueDate,
  }));
};